import { Result, Spin } from "antd";
import { showError } from "common/ToastNotifications";
import AddAnnotationForm from "components/molecules/AddAnnotationForm";
import AnnotationList from "components/molecules/AnnotationList";
import { Component } from "react";
import { connect } from "react-redux";
import actions from "store/actions";
import { getUsersRequest } from "store/users/actions";

class Annotations extends Component {
    constructor(props) {
        super(props);
        this.baseServerFilters = {
            type: undefined,
            state: undefined,
            priority: undefined,
            assignedTo: undefined,
            sustained: undefined,
            closed: undefined,
            limit: 20,
            sortOrder: "desc",
        };
        const initialServerFilters = props.initialServerFilters ? { ...this.baseServerFilters, ...props.initialServerFilters } : { ...this.baseServerFilters };
        this.state = {
            busy: false,
            busyMessage: "",
            creating: false,
            listLoading: false,
            serverFilters: initialServerFilters,
        };
        this.defaultServerFilters = { ...this.baseServerFilters };
    }

    componentDidMount() {
        const { recordId, recordType } = this.props;
        if (recordId && recordType) {
            this.loadAnnotations(recordType, recordId, this.state.serverFilters);
        }
        this.props.getUsers();
    }

    componentDidUpdate(prevProps) {
        if (this.props.recordId !== prevProps.recordId || this.props.recordType !== prevProps.recordType) {
            this.resetFiltersAndReload(this.props.initialServerFilters);
        }

        this.onGetAnnotationsResultUpdate(prevProps);
        this.onCreateAnnotationResultUpdate(prevProps);
        this.onUpdateAnnotationResultUpdate(prevProps);
        this.onDeleteAnnotationResultUpdate(prevProps);
    }

    loadAnnotations(recordType, recordId, queryParams = this.state.serverFilters, options = {}) {
        if (!recordId || !recordType) return;
        const { message = "Loading annotations...", listOnly = false } = options;
        this.setState({
            busy: listOnly ? false : true,
            busyMessage: listOnly ? "" : message,
            listLoading: true,
        });
        this.props.getAnnotations(recordType, recordId, queryParams);
    }

    resetFiltersAndReload(overrides) {
        const { recordType, recordId } = this.props;
        const resetFilters = overrides ? { ...this.baseServerFilters, ...overrides } : { ...this.baseServerFilters };
        this.setState({ serverFilters: resetFilters }, () => {
            this.loadAnnotations(recordType, recordId, resetFilters);
        });
    }

    onGetAnnotationsResultUpdate(prevProps) {
        const { getAnnotationsResult } = this.props;
        if (getAnnotationsResult !== prevProps.getAnnotationsResult) {
            if (getAnnotationsResult?.loading) {
                return;
            }

            this.setState({ busy: false, busyMessage: "", listLoading: false });
            if (getAnnotationsResult?.error) {
                showError("Failed to load annotations");
            }
        }
    }

    onCreateAnnotationResultUpdate(prevProps) {
        const { createAnnotationResult, recordId, recordType } = this.props;
        if (createAnnotationResult !== prevProps.createAnnotationResult) {
            const requestCompleted = createAnnotationResult?.success || createAnnotationResult?.error;
            if (requestCompleted) {
                this.setState({ creating: false });
                if (createAnnotationResult.success) {
                    this.loadAnnotations(recordType, recordId);
                }
            }
        }
    }

    onUpdateAnnotationResultUpdate(prevProps) {
        const { updateAnnotationResult, recordId, recordType } = this.props;
        if (updateAnnotationResult !== prevProps.updateAnnotationResult && updateAnnotationResult?.success) {
            this.loadAnnotations(recordType, recordId);
        }
    }

    onDeleteAnnotationResultUpdate(prevProps) {
        const { deleteAnnotationResult, recordId, recordType } = this.props;
        if (deleteAnnotationResult !== prevProps.deleteAnnotationResult && deleteAnnotationResult?.success) {
            this.loadAnnotations(recordType, recordId);
        }
    }

    handleAddAnnotation = (annotationData) => {
        const { recordId, recordType, businessAreaId } = this.props;
        const redirectUrl = typeof window !== "undefined" ? window.location.href : null;
        const payload = {
            ...annotationData,
            recordType,
            recordId,
            ...(businessAreaId && { businessAreaId }),
            redirect_url: redirectUrl,
        };
        this.setState({ creating: true });
        this.props.createAnnotation(recordType, recordId, payload);
    };

    handleUpdateAnnotation = (annotationId, annotationData) => {
        const { recordId, recordType } = this.props;
        const redirectUrl = typeof window !== "undefined" ? window.location.href : null;
        const payload = {
            ...annotationData,
            recordType,
            recordId,
            redirect_url: redirectUrl,
        };
        this.props.updateAnnotation(recordType, recordId, annotationId, payload);
    };

    handleDeleteAnnotation = (annotationId) => {
        const { recordId, recordType } = this.props;
        this.props.deleteAnnotation(recordType, recordId, annotationId);
    };

    handleServerFiltersChange = (updatedFilters = {}) => {
        this.setState(
            (prevState) => ({
                serverFilters: {
                    ...prevState.serverFilters,
                    ...updatedFilters,
                },
            }),
            () => {
                const { recordType, recordId } = this.props;
                this.loadAnnotations(recordType, recordId, this.state.serverFilters, { message: "Filtering annotations...", listOnly: true });
            },
        );
    };

    handleServerFiltersReset = () => {
        const resetFilters = { ...this.defaultServerFilters };
        this.setState({ serverFilters: resetFilters }, () => {
            const { recordType, recordId } = this.props;
            this.loadAnnotations(recordType, recordId, resetFilters, { listOnly: true, message: "Filtering annotations..." });
        });
    };

    render() {
        const { annotations, users, getAnnotationsResult, permission, fields = [], recordId, recordType, historyDisplayMode } = this.props;
        const { busy, busyMessage, serverFilters, creating, listLoading } = this.state;
        const listSpinnerActive = listLoading && !busy;
        const recordKey = recordId && recordType ? `${recordType}:${recordId}` : undefined;

        if (getAnnotationsResult && getAnnotationsResult.error) {
            return <Result status="error" title="Error" subTitle="Sorry, something went wrong while loading annotations." />;
        }

        return (
            <Spin spinning={busy} tip={busyMessage} style={{ height: "100%" }} classNames={{ wrapper: "full-height-spin" }}>
                <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: "24px", background: "#fff" }}>
                    <div style={{ flex: "1 1 auto", overflow: "hidden" }}>
                        <AnnotationList
                            annotations={annotations}
                            users={users}
                            fields={fields}
                            onUpdate={this.handleUpdateAnnotation}
                            onDelete={this.handleDeleteAnnotation}
                            permission={permission}
                            recordKey={recordKey}
                            serverFilters={serverFilters}
                            defaultServerFilters={this.defaultServerFilters}
                            onServerFiltersChange={this.handleServerFiltersChange}
                            onServerFiltersReset={this.handleServerFiltersReset}
                            isLoading={listSpinnerActive}
                            historyDisplayMode={historyDisplayMode}
                        />
                    </div>
                    {permission?.canAdd && (
                        <div style={{ paddingTop: "16px", flexShrink: 0, background: "#fff", padding: "0px 12px", margin: "0 -24px -24px" }}>
                            <AddAnnotationForm users={users} fields={fields} onSubmit={this.handleAddAnnotation} isSubmitting={creating} />
                        </div>
                    )}
                </div>
            </Spin>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const { recordId, recordType } = ownProps;
    const recordKey = `${recordType}:${recordId}`;
    const annotationsForRecord = state.annotations.annotationsByRecordKey?.[recordKey]?.Items || [];

    return {
        annotations: annotationsForRecord,
        users: state.users?.userList || [],
        getAnnotationsResult: state.annotations.getAnnotationsResult,
        createAnnotationResult: state.annotations.createAnnotationResult,
        updateAnnotationResult: state.annotations.updateAnnotationResult,
        deleteAnnotationResult: state.annotations.deleteAnnotationResult,
    };
};

const mapDispatchToProps = (dispatch) => ({
    getAnnotations: (recordType, recordId, queryParams) => dispatch(actions.getAnnotationsRequest(recordType, recordId, queryParams)),
    createAnnotation: (recordType, recordId, annotationData) => dispatch(actions.createAnnotationRequest(recordType, recordId, annotationData)),
    updateAnnotation: (recordType, recordId, annotationId, annotationData) => dispatch(actions.updateAnnotationRequest(recordType, recordId, annotationId, annotationData)),
    deleteAnnotation: (recordType, recordId, annotationId) => dispatch(actions.deleteAnnotationRequest(recordType, recordId, annotationId)),
    getUsers: () => dispatch(getUsersRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Annotations);
